// @ts-check

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { createContext, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import GuestChatProvider from "./guest-chat-provider"
import useGuestChatRestoration from "./useGuestChatRestoration"

import {
  useActiveChatSessionId,
  useChatSessionList,
  useActiveChatSession,
  useCreateChatSession,
  useUpdateChatSession,
  useDeleteChatSession,
} from './sessions'

import {
  useActiveProjectId,
  useChatProjects,
  useCreateChatProject,
} from './projects'

import {
  useAgentMode,
  useModelConfiguration,
  useInputMessage,
  useSendChatMessage,
  useEditChatMessage,
  useRegenerateChatMessage,
  useSendAgentMessage,
  useSendChatFeedback,
  useUploadFile,
  useActiveChatMessages,
} from './messages'

import {
  useActiveChatSessionReports,
  useGenerateReport,
  useUpdateReport,
} from './reports'

import {
  useActiveChatSessionImages,
  useChatSessionThumbnailImage,
  useUploadChatSessionImage,
} from './images'

import {
  useStreamingMessage,
  useStreamingReport,
  useActiveSessionHasActiveStream,
  useGetChatSessionStream,
  useCancelActiveChatStream,
} from './streams'

function useGeolocation() {
  return useQuery({
    queryKey: ["geolocation"],
    queryFn: async () => {
      const res = await fetch("https://ipapi.co/json/");
      if (!res.ok) throw new Error("Failed to fetch geolocation data", { cause: res })

      const data = await res.json()
      return {
        city: data?.city,
        region: data?.region ?? data?.regionName,
        country: data?.country_name,
        latitude: data?.latitude,
        longitude: data?.longitude,
      }
    },
    staleTime: Infinity,
    enabled: false // ? Not used atm, had some errors in other locations, don't know if fixed
  })
}

function useIsGuestMode() {
  return false
}

function useGuestMessages() {
  return /**@type{ChatMessage[]}*/(null)
}

const ChatContextValues = {
  useActiveChatSessionId,
  useChatSessionList,
  useActiveChatSession,
  useCreateChatSession,
  useUpdateChatSession,
  useDeleteChatSession,

  useActiveProjectId,
  useChatProjects,
  useCreateChatProject,

  useAgentMode,
  useModelConfiguration,
  useInputMessage,
  useSendChatMessage,
  useEditChatMessage,
  useRegenerateChatMessage,
  useSendAgentMessage,
  useSendChatFeedback,
  useUploadFile,
  useActiveChatMessages,

  useActiveChatSessionReports,
  useGenerateReport,
  useUpdateReport,

  useActiveChatSessionImages,
  useChatSessionThumbnailImage,
  useUploadChatSessionImage,

  useStreamingMessage,
  useStreamingReport,
  useActiveSessionHasActiveStream,
  useGetChatSessionStream,
  useCancelActiveChatStream,

  useGeolocation,
  useIsGuestMode,
  useGuestMessages,
}
/** @typedef {typeof ChatContextValues} ChatContextValues */

const ChatContext = createContext(/** @type {ChatContextValues} */({}))

function useChatContext() {
  return useContext(ChatContext)
}

/**
 * @param {Object} props 
 * @param {React.ReactNode} props.children 
 */
function ChatProvider({ children }) {
  const queryClient = useQueryClient()
  const { mutateAsync: createNewChatSession } = useCreateChatSession()
  const { mutateAsync: updateSession } = useUpdateChatSession()

  useGuestChatRestoration({
    createNewChatSession,
    updateSession,
    // TODO: Find better way to pass isInitialised or remove entirely
    isInitialized: true,
  })

  const { user } = useSelector(state => state.app.datastate)
  const isLoggedIn = user?._id != null
  useEffect(() => {
    if(!isLoggedIn) return
    return () => queryClient.clear()
  }, [isLoggedIn])


  return (
    <ChatContext value={ChatContextValues}>
      {children}
    </ChatContext>
  )
}

/**
 * @param {Object} props
 * @param {React.ReactNode | ((props: ChatContextValues) => React.ReactNode)} props.children
 */
function WithChatContext({ children }) {
  let props = useContext(ChatContext)
  if(typeof children === "function") return children(props)
  return children
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
function UniversalChatProvider({ children }) {
  const { user } = useSelector(state => state.app.datastate) || {};
  const isLoggedIn = !!(user && user._id);

  if (!isLoggedIn) {
    return <GuestChatProvider>{children}</GuestChatProvider>;
  }

  return <ChatProvider>{children}</ChatProvider>;
}

export default UniversalChatProvider
export {
  REVERT_MESSAGE_EVENT,
  OPEN_REPORT_PANEL_EVENT,
  OPEN_REPORT_FORM_EVENT,
  UNSENT_FILE_ID,
  AgentMode,
  AI_USER,
  AGENT_USER,
  DEEP_RESEARCH_USER,
  STARTING_MESSAGE_EDIT_EVENT,
  isStartingMessageEditEvent,
} from "./utils"
export {
  // useActiveChatSessionId,
  // useChatSessionList,
  // useActiveChatSession,
  // useCreateChatSession,
  // useUpdateChatSession,
  // useDeleteChatSession,

  // useActiveProjectId,
  // useChatProjects,
  // useCreateChatProject,

  // useAgentMode,
  // useModelConfiguration,
  // useInputMessage,
  // useSendChatMessage,
  // useEditChatMessage,
  // useRegenerateChatMessage,
  // useSendAgentMessage,
  // useSendChatFeedback,
  // useUploadFile,

  // useActiveChatSessionReports,
  // useGenerateReport,
  // useUpdateReport,

  // useActiveChatSessionImages,
  // useChatSessionThumbnailImage,
  // useUploadChatSessionImage,

  // useStreamingMessage,
  // useStreamingReport,
  // useGetChatSessionStream,
  // useCancelActiveChatStream,

  // useGeolocation,
  // useIsGuestMode,
  // useGuestMessages,

  ChatContext,
  useChatContext,
  ChatProvider,
  WithChatContext,
}
